import { useCallback, useEffect, useRef, useState } from 'react'
import { transformPresets, useViewportScale } from 'src/hooks/useViewportScale'
import { createPortal } from 'react-dom'
import { useGetCurrentGame, useGetCurrentLanguage } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { useClientSideTranslation } from 'src/services/i18n'
import { isRaceSettled } from 'src/utils/raceUtils'
import { isCrashGameType } from 'src/utils/gameTypeUtils'
import { useVisualCrashStatus } from 'src/components/CrashChipPanel/useVisualCrash'
import { resolveCrashEventInfo } from 'src/components/CrashChipPanel/crashEventInfo'
import { useDeepRaceHistory } from 'src/hooks/useDeepRaceHistory'
import { mergeRaceHistory } from 'src/utils/mergeRaceHistory'
import TimesIcon from 'src/app/assets/icons/ico-times.svg'
import { RoundStats } from 'src/components/PastRacesSidebar/components/RoundStats'

type Props = Readonly<{
  onClose: () => void
}>

/**
 * The recent-form card as a dismissible popover — bottom-right on desktop,
 * a bottom overlay on mobile. Same numbers as the past-races panel; this is
 * just a faster way to reach them without leaving the table.
 *
 * Shares useDeepRaceHistory with the sidebar panel, so opening both inside the
 * cache TTL costs one read, not two.
 */
export function RoundStatsPopover({ onClose }: Props) {
  const game = useGetCurrentGame()
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const visualCrashStatus = useVisualCrashStatus()
  const cardRef = useRef<HTMLDivElement>(null)

  const gameId = isMarbleGame(game) ? game.id : undefined
  const { races: deepRaces, isLoading } = useDeepRaceHistory(gameId)

  // Esc closes, and focus moves into the card on open so a keyboard user is
  // not left behind on the toggle button.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    cardRef.current?.focus()
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // Only the mobile scrim closes on an outside click. On desktop the panel is
  // a companion you keep open while playing — the scrim there is
  // pointer-events: none, so clicks reach the game and never dismiss it.
  const handleScrimClick = useCallback(() => onClose(), [onClose])

  // Same scale the other floating panels ride, so the card shrinks with the
  // window instead of crowding the table — and the same right-panel preset,
  // which carries the translateY(-50%) that centres it against the past-round
  // panel it sits beside. No-ops on mobile (the hook opts out there), where
  // the card keeps its own bottom-sheet layout.
  const scaleStyle = useViewportScale({
    transform: transformPresets.rightFloatingPanel,
    transformOrigin: 'right center'
  })

  // Sit 10px to the left of the right-hand rail. Measured rather than
  // hard-coded: that rail is as wide as its contents, which differ per game
  // (a crash multiplier strip is nothing like a marble column), so any fixed
  // offset is wrong for some game. Falls back to a sane constant if the rail
  // is not mounted — mobile, or a game with the interface hidden.
  const [railOffset, setRailOffset] = useState<number | undefined>()

  useEffect(() => {
    const measure = () => {
      const rail = document.querySelector('.right-floating-panel')
      if (!rail) {
        setRailOffset(undefined)
        return
      }
      const { left, width } = rail.getBoundingClientRect()
      // A zero-width rail is mid-animation; keep the previous offset.
      if (width === 0) return
      setRailOffset(Math.max(Math.round(window.innerWidth - left) + 10, 0))
    }

    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  if (typeof document === 'undefined' || !isMarbleGame(game)) {
    return null
  }

  // Same anti-spoiler hold the pills and the past-races rows wear: while the
  // newest crash is known-but-not-yet-seen, keep it out of the window — of the
  // list AND of the denominator, or this card reveals the bust early.
  const allRaces = mergeRaceHistory(game.races, deepRaces)
  // Anchored on the merged window's newest round, not the store's — same
  // reasoning as PastRacesSidebar.
  const latestCrashRace = isCrashGameType(game.track.gameType) ? allRaces[0] : undefined
  const holdNewestId =
    latestCrashRace &&
    visualCrashStatus === 'armed' &&
    resolveCrashEventInfo(latestCrashRace.raceEvents).crashKnown
      ? latestCrashRace.uuid
      : null

  const races = allRaces
    .filter(isRaceSettled)
    .filter((race) => race.uuid !== holdNewestId)

  const popover = (
    // The scrim is what makes "click anywhere else" close it. It is
    // transparent on desktop (the popover is a corner card, not a modal) and
    // dims on mobile, where the card reads as a temporary overlay.
    <div className="round-stats-popover__scrim" onClick={handleScrimClick} role="presentation">
      {/* Two elements on purpose. The OUTER one owns position and the viewport
          scale transform; the INNER one owns the look and the entry
          animation. They cannot share a node: an animation that touches
          `transform` overrides the inline `scale(s) translateY(-50%)` for its
          whole duration, so the card used to open from half a card too low
          and snap into place. */}
      <div
        className="round-stats-popover"
        style={railOffset === undefined ? scaleStyle : { ...scaleStyle, right: railOffset }}
      >
        <div
          aria-label={t('Recent form')}
          className="round-stats-popover__card"
          onClick={(e) => e.stopPropagation()}
          ref={cardRef}
          role="dialog"
          tabIndex={-1}
        >
          {/* No title bar: the window line says what this is, and naming the
              game restated something the player is looking at. The close sits
              over the card's own header instead of adding a second row. */}
          <button
            aria-label={t('Close')}
            className="round-stats-popover__close"
            onClick={onClose}
            type="button"
          >
            <TimesIcon />
          </button>
          <RoundStats compact game={game} isLoading={isLoading} races={races} />
        </div>
      </div>
    </div>
  )

  return createPortal(popover, document.body)
}
